/**
 * SE-1882 Coaching page — the T13 destination for the `person` axis,
 * with the assign / revoke workflow.
 *
 * Copy-then-diverge from `RoleAxisPage.tsx` per this ticket's plan
 * (SE-1882 §Outcome step 4): the shared skeleton proved the pickers
 * and the T12 delegated-caller wire; the Coaching page owns the
 * grants table, the assign dialog and the per-mentee revoke button.
 * The Rotations page still uses the shared skeleton until SE-1883
 * specializes it.
 *
 * DATA FLOW (SE-1817 EX-2):
 *
 *   - **Existing grants table**: `GET /api/role-scopes?roles=Coach&
 *     scope_type=person&program_id=<uuid>` → `groupByCoach` folds
 *     `role_scopes[]` into one row per coach (mirror of
 *     `coaching_service.group_by_coach` in the internal template so
 *     the two admin UIs cannot drift on what "one coach" means).
 *   - **Grantee picker**: active Attending `programMembership` rows
 *     in the selected program, via
 *     `fetchUsersByProgramIds(programIds, ProgramRole.Attending, true)`.
 *     Chosen 2026-08-28 (SE-1882 Research & Decisions #1) over "any
 *     email" — the picker offers exactly the people whose downstream
 *     Attending-shaped assessments will actually load, so a coach
 *     grant never lands on a UI dead end.
 *   - **Mentee picker**: `GET /api/scope-choices?axis=person&
 *     program_id=<uuid>` — same call the T13 skeleton uses; the
 *     provider is server-narrowed to persons active in the program.
 *   - **Save**: `GET /api/get-user-access?email=<coach>` → the coach's
 *     current flat `roles` + `entitlements` (PRESERVED verbatim on the
 *     outbound PUT; see api/get-user-access/src/index.ts for why a
 *     bare `["Coach"]` silently demotes a Coach + ProgramAdmin) →
 *     `PUT /api/user-access` with `person_scopes.Coach` = the coach's
 *     WHOLE mentee set after the change (declarative set-difference;
 *     the server computes add / archive).
 *   - **Revoke**: same PUT path, with the row's `scope_id` omitted
 *     from the outbound `person_scopes.Coach`. Only offered on ADMIN-
 *     provenance rows (SE-1819 D3: a synced grant gets re-minted on
 *     the next sync, so a revoke button on it is a lie).
 *
 * PROVENANCE (SE-1819 D3). `data_source_name` other than `ADMIN` MUST
 * render read-only. The `isAdminOwned` predicate is imported (not
 * inlined) so the internal template and this page cannot disagree on
 * what "ours to change" means; the write path re-validates.
 *
 * NO T6 DEBUG PANEL. It was the T13 acceptance's only visible signal
 * that the T12 narrowing was happening; now that the grants table
 * fetches through the same delegated-caller wire, THAT is the visible
 * signal (an internal-staff caller sees every coach in the program;
 * a ProgramAdmin sees only their own program's rows). The debug
 * panel stays on Rotations until SE-1883 lands its own write path.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";

import { ProgramRole } from "../../API";
import { useAppSelector } from "../../app/hooks";
import { selectUserRoles } from "../Users/usersSlice";
import { Role } from "../Auth/roles";
import { PageWrapper } from "../../common-ui/PageWrapper";
import { getProgramsCurrentUserCanAdminister } from "../../app/utils";
import {
  alphabetizePrograms,
  programLabel,
  programsVisibleTo,
  useAllPrograms,
} from "../Programs/programUtils";
import { selectNetworkFeaturesByProgramId } from "../Networks/networkFeaturesSlice";
import { fetchUsersByProgramIds } from "../Users/usersAPI";

import { useKnownRoles, useRoleScopes, useScopeChoices } from "./hooks";
import {
  Coach,
  RoleScope,
  ScopeBinding,
  apiErrorMessage,
  fetchUserAccess,
  groupByCoach,
  isAdminOwned,
  nextScopeSet,
  putUserAccess,
  rolesOnAxis,
} from "./simplAuthAPI";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type SaveKind = "assign" | "revoke";

// A single grantee candidate for the coach picker. Kept intentionally
// minimal (`id` + `email` + display `name`) so the picker doesn't leak
// the rest of the `ProgramUser` shape into this page's business logic.
type Grantee = {
  id: string;
  email: string;
  displayName: string;
};

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

// Coach-picker candidate set: active Attending and Coach programMemberships
// in the selected program, minus anyone who is also a Trainee there.
// Deduplicates by email (a user with two memberships in one program still
// lists once) and shapes into the picker's `Grantee` tuple. Also returns
// the program's active Trainee ids — the only people the mentee picker
// offers.
// SE-1765: coaches are Attendings, or people invited as a Coach (e.g.
// faculty from outside the program); mentees are Trainees. `PUT
// /user-access` refuses anything else, and on a successful assignment
// gives an Attending coach the Coach programmembership in the program, so
// nobody has to add that role through Edit User first.
const coachingRoster = async (
  programId: string,
): Promise<{ coaches: Grantee[]; traineeIds: Set<string> }> => {
  if (!programId) return { coaches: [], traineeIds: new Set() };
  const [attendings, coachRows, trainees] = await Promise.all([
    fetchUsersByProgramIds([programId], ProgramRole.Attending, true),
    fetchUsersByProgramIds([programId], ProgramRole.Coach, true),
    fetchUsersByProgramIds([programId], ProgramRole.Trainee, true),
  ]);
  const traineeIds = new Set((trainees ?? []).map((row) => row.id));
  const rows = [...(attendings ?? []), ...(coachRows ?? [])];
  const seen = new Set<string>();
  const out: Grantee[] = [];
  for (const row of rows) {
    const email = (row.email ?? "").trim().toLowerCase();
    if (!email || seen.has(email) || traineeIds.has(row.id)) continue;
    seen.add(email);
    const fullName = [row.firstName, row.lastName]
      .map((s) => (s ?? "").trim())
      .filter(Boolean)
      .join(" ");
    out.push({
      id: row.id,
      email,
      displayName: fullName || email,
    });
  }
  out.sort((a, b) => a.displayName.localeCompare(b.displayName));
  return { coaches: out, traineeIds };
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export const CoachingPage = () => {
  const { t } = useTranslation();

  // --- Programs the current caller can administer ---
  const { programs: administeredPrograms } =
    getProgramsCurrentUserCanAdminister();
  const {
    status: programsStatus,
    error: programsError,
    getAllPrograms,
  } = useAllPrograms();

  const retryLoadPrograms = () => {
    void getAllPrograms();
  };

  const currentUsersRoles = useAppSelector(selectUserRoles);
  const isSysAdmin = !!currentUsersRoles?.includes(Role.SystemAdmin);
  // SE-1883: filter the picker to programs whose network has the
  // coaching feature. `byProgramId` is populated for both SysAdmin
  // (every program) and ProgramAdmin (their own memberships); the
  // Lambda's SysAdmin branch preserves the same shape so this filter
  // is caller-agnostic. Programs missing from the map fall through
  // as "no feature" (safe default: hide).
  const featuresByProgramId = useAppSelector(selectNetworkFeaturesByProgramId);

  const programs = useMemo(() => {
    const eligible = (administeredPrograms ?? []).filter(
      (p): p is NonNullable<typeof p> => !!p && !!p.id && !!p.name
    );
    // A program whose institution is deleted is only offered to a
    // SystemAdmin, and is labelled as such — see `programsVisibleTo`.
    const visible = programsVisibleTo(eligible, isSysAdmin);
    // SE-1883: only show programs whose network has coaching enabled.
    const withCoaching = visible.filter(
      (p) => featuresByProgramId[p.id]?.coaching === true,
    );
    // Institution-major order (the `true`), so the dozens of programs
    // sharing a specialty name group under their institution instead of
    // scattering down the list. `programLabel` is what makes them
    // distinguishable once they're there.
    return alphabetizePrograms(withCoaching, true).map((p) => ({
      id: p.id,
      label: programLabel(p),
    }));
  }, [administeredPrograms, isSysAdmin, featuresByProgramId]);

  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  useEffect(() => {
    if (programs.length > 0 && !selectedProgramId) {
      setSelectedProgramId(programs[0].id);
    }
  }, [programs, selectedProgramId]);

  // --- Role catalog (T6) — coaching manages the person-axis roles ---
  const catalog = useKnownRoles();
  const personAxisRoles = useMemo(() => {
    if (!catalog.data) return [];
    return rolesOnAxis(catalog.data, "person");
  }, [catalog.data]);

  // Today the person axis carries only "Coach" — but this page never
  // types the literal. If simpl_entitlements' catalog grows a second
  // person-axis role, it appears here with no edit (SE-1817 EX-2).
  const managedRoles = personAxisRoles;

  // --- Existing grants: the by-scope read for the whole program ---
  const grants = useRoleScopes(
    managedRoles,
    selectedProgramId ? "person" : null,
    selectedProgramId || null,
  );

  const coaches: Coach[] = useMemo(() => {
    if (!grants.data) return [];
    return groupByCoach(grants.data.role_scopes);
  }, [grants.data]);

  // --- Mentee picker population (scope-choices axis=person) ---
  const mentees = useScopeChoices(
    selectedProgramId ? "person" : null,
    selectedProgramId || null,
  );

  // --- Coach picker population (Turso: active Attendings and Coaches in program) ---
  const [attendings, setAttendings] = useState<Grantee[]>([]);
  const [attendingsStatus, setAttendingsStatus] = useState<
    "idle" | "loading" | "succeeded" | "failed"
  >("idle");
  const [attendingsError, setAttendingsError] = useState<string | null>(null);
  const [traineeIds, setTraineeIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!selectedProgramId) {
      setAttendings([]);
      setTraineeIds(new Set());
      setAttendingsStatus("idle");
      return;
    }
    let cancelled = false;
    setAttendingsStatus("loading");
    setAttendingsError(null);
    coachingRoster(selectedProgramId)
      .then(({ coaches, traineeIds }) => {
        if (cancelled) return;
        setAttendings(coaches);
        setTraineeIds(traineeIds);
        setAttendingsStatus("succeeded");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setAttendingsError(message);
        setAttendingsStatus("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProgramId]);

  // --- Save state ---
  const [savingRow, setSavingRow] = useState<
    { kind: SaveKind; email: string; scopeId?: string } | null
  >(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const runSave = async (
    coachEmail: string,
    role: string,
    // The ONE change to make. The coach's other mentees — every program,
    // as of now — are read fresh and carried by `nextScopeSet` (SE-2841).
    change: { add?: ScopeBinding[]; remove?: string[] },
    kind: SaveKind,
    // Human-readable names for the two participants — the caller has
    // them (assign has `assignCoach.displayName` + `menteeChoice.label`,
    // revoke has the attendings-map lookup + `row.scope_name`). We keep
    // the copy in the save site so a `t()` fallback ("this coach", "this
    // mentee") doesn't get baked into `runSave` and re-surface elsewhere.
    personNames: { coachName: string; menteeName: string },
    scopeIdForBadge?: string,
  ) => {
    setSaveError(null);
    setSaveSuccess(null);
    setSavingRow({ kind, email: coachEmail, scopeId: scopeIdForBadge });
    try {
      const currentAccess = await fetchUserAccess(coachEmail);
      // The coach's whole mentee set after this change. The Lambda works
      // out the flat roles from it, and ends the Coach label in a program
      // whose last mentee goes (SE-3027).
      const outbound = nextScopeSet(
        currentAccess?.role_scopes ?? [],
        role,
        "person",
        change,
      );

      const result = await putUserAccess({
        email: coachEmail,
        person_scopes: { [role]: outbound },
      });
      if (result.changed) {
        setSaveSuccess(
          kind === "assign"
            ? t("{{coach}} now coaches {{mentee}}.", {
                coach: personNames.coachName,
                mentee: personNames.menteeName,
              })
            : t("{{coach}} no longer coaches {{mentee}}.", {
                coach: personNames.coachName,
                mentee: personNames.menteeName,
              }),
        );
      } else {
        setSaveSuccess(
          t("Nothing to update — that assignment was already in place."),
        );
      }
      grants.reload();
    } catch (err: unknown) {
      const fallback = err instanceof Error ? err.message : String(err);
      setSaveError(apiErrorMessage(err, fallback));
      grants.reload();
    } finally {
      setSavingRow(null);
    }
  };

  // --- Assign form ---
  const [assignCoach, setAssignCoach] = useState<Grantee | null>(null);
  const [assignMenteeId, setAssignMenteeId] = useState<string>("");
  const [assignRole, setAssignRole] = useState<string>("");
  useEffect(() => {
    if (managedRoles.length === 0) {
      if (assignRole) setAssignRole("");
      return;
    }
    if (!assignRole || !managedRoles.includes(assignRole)) {
      setAssignRole(managedRoles[0]);
    }
  }, [managedRoles, assignRole]);

  // Reset the assign form whenever the program changes.
  useEffect(() => {
    setAssignCoach(null);
    setAssignMenteeId("");
    setSaveError(null);
    setSaveSuccess(null);
  }, [selectedProgramId]);

  // Existing-pairs index for the assign form's option-disable logic.
  //
  // The user's mental model of "already coaches" is provenance-blind: if
  // Ada already coaches Mary — SIMULATED_SSO or ADMIN, doesn't matter —
  // then offering the pair as an "Assign" click is a no-op at best and a
  // duplicate-provenance mint at worst (an ADMIN row inserted alongside
  // the existing SSO row). So this index folds in every mentee for every
  // coach, and drives the two `getOptionDisabled` / `MenuItem disabled`
  // predicates below.
  //
  // Note this is orthogonal to `nextScopeSet()`'s provenance filter:
  // that one shapes the outbound `person_scopes` set (ADMIN-only) so
  // simpl_auth's declarative diff doesn't archive foreign rows. Here
  // we're picking who is OFFERED as a candidate, which is the opposite
  // direction — pre-write UX, not the write's payload.
  //
  // Role-scoped so a future two-role Coaching page (SE-1817 EX-2 opens
  // that door) doesn't cross-contaminate the two axes.
  const existingPairs = useMemo(() => {
    const menteesByCoach = new Map<string, Set<string>>();
    const coachesByMentee = new Map<string, Set<string>>();
    for (const c of coaches) {
      const email = c.email.toLowerCase();
      for (const m of c.mentees) {
        if (m.role !== assignRole || !m.scope_id) continue;
        if (!menteesByCoach.has(email))
          menteesByCoach.set(email, new Set());
        menteesByCoach.get(email)!.add(m.scope_id);
        if (!coachesByMentee.has(m.scope_id))
          coachesByMentee.set(m.scope_id, new Set());
        coachesByMentee.get(m.scope_id)!.add(email);
      }
    }
    return { menteesByCoach, coachesByMentee };
  }, [coaches, assignRole]);

  // Mentees to gray out given the currently-selected coach. Empty when
  // no coach is selected — that's the "no side selected, show all"
  // path.
  const disabledMenteeIds = useMemo(() => {
    if (!assignCoach) return new Set<string>();
    return (
      existingPairs.menteesByCoach.get(assignCoach.email.toLowerCase()) ??
      new Set<string>()
    );
  }, [existingPairs, assignCoach]);

  // Coaches to gray out given the currently-selected mentee.
  const disabledCoachEmails = useMemo(() => {
    if (!assignMenteeId) return new Set<string>();
    return existingPairs.coachesByMentee.get(assignMenteeId) ?? new Set<string>();
  }, [existingPairs, assignMenteeId]);

  // If the other side's selection SWITCHED (a different coach picked,
  // say), the previously-picked mentee may now be in the disabled set —
  // clear it. Same for the reverse. Without this, the picker shows a
  // selected-but-grayed value that would submit an idempotent no-op.
  //
  // The two effects DO NOT compose into a race: MUI Select's `disabled`
  // guard rejects new clicks on greyed options, so a user cannot land in
  // a state where both sides are simultaneously in each other's disabled
  // set — only one side changes at a time.
  useEffect(() => {
    if (assignMenteeId && disabledMenteeIds.has(assignMenteeId)) {
      setAssignMenteeId("");
    }
  }, [disabledMenteeIds, assignMenteeId]);
  useEffect(() => {
    if (
      assignCoach &&
      disabledCoachEmails.has(assignCoach.email.toLowerCase())
    ) {
      setAssignCoach(null);
    }
  }, [disabledCoachEmails, assignCoach]);

  // Mentees are Trainees only; scope-choices offers every active person
  // in the program, keyed by the same user id Turso uses.
  const traineeMentees = useMemo(
    () => (mentees.data?.choices ?? []).filter((c) => traineeIds.has(c.id)),
    [mentees.data, traineeIds],
  );
  const menteesStatus =
    mentees.status === "failed" || attendingsStatus === "failed"
      ? "failed"
      : mentees.status === "succeeded" && attendingsStatus === "succeeded"
        ? "succeeded"
        : mentees.status === "idle"
          ? "idle"
          : "loading";

  const canAssign =
    assignCoach !== null &&
    assignMenteeId !== "" &&
    assignRole !== "" &&
    savingRow === null;

  // Email → display name, so save messages and the grants table both
  // show "Ada Lovelace" instead of "ada.lovelace@demo.simpl.edu".
  // `list_role_scopes` only returns email for the coach; the display
  // name comes from Turso via `fetchUsersByProgramIds` (already loaded
  // as `attendings`). A coach not in the Attendings list (unusual —
  // coaching in a program they aren't Attending in) falls back to
  // email.
  const attendingNameByEmail = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of attendings) {
      if (a.email && a.displayName) {
        map.set(a.email.toLowerCase(), a.displayName);
      }
    }
    return map;
  }, [attendings]);

  const onAssign = async () => {
    if (!canAssign || !assignCoach) return;
    const menteeChoice = mentees.data?.choices.find(
      (c) => c.id === assignMenteeId,
    );
    if (!menteeChoice) return;

    // If this mentee is already bound to this coach — under ANY
    // provenance — refuse the click. The picker's `getOptionDisabled`
    // already grays such pairs, but a stale grants snapshot (e.g., a
    // sync landed between render and click) or a keyboard shortcut past
    // a disabled option could still slip through. Two escape hatches:
    //   ADMIN pair: idempotent no-op server-side, but we short-circuit
    //     to save the round trip.
    //   Foreign (SIMULATED_SSO / MEDQIP) pair: a duplicate-provenance
    //     mint — the server would add an ADMIN row alongside the
    //     existing foreign one, splitting one visible binding across
    //     two rows in the grants table. Refuse.
    const emailKey = assignCoach.email.toLowerCase();
    const alreadyPaired =
      existingPairs.menteesByCoach.get(emailKey)?.has(menteeChoice.id) ??
      false;
    if (alreadyPaired) {
      setSaveError(
        t("{{coach}} already coaches {{mentee}}.", {
          coach: assignCoach.displayName,
          mentee: menteeChoice.label,
        }),
      );
      return;
    }

    await runSave(
      assignCoach.email,
      assignRole,
      { add: [{ scope_id: menteeChoice.id, scope_name: menteeChoice.label }] },
      "assign",
      { coachName: assignCoach.displayName, menteeName: menteeChoice.label },
    );
    setAssignCoach(null);
    setAssignMenteeId("");
  };

  const onRevoke = async (coach: Coach, row: RoleScope) => {
    if (!isAdminOwned(row.data_source_name)) return;
    if (!row.role) return;
    const coachName =
      attendingNameByEmail.get(coach.email.toLowerCase()) ?? coach.email;
    const menteeName = row.scope_name ?? row.scope_id ?? "";
    await runSave(
      coach.email,
      row.role,
      { remove: [row.scope_id] },
      "revoke",
      { coachName, menteeName },
      row.scope_id,
    );
  };

  // --- Render ---

  const programBlockedByEnv =
    programsStatus === "loading" || programsStatus === "idle";
  const programsFailed = programsStatus === "failed";
  const hasPrograms = programs.length > 0;

  return (
    <PageWrapper className="Coaching" headerText="Coaching">
      <Typography variant="body1" sx={{ mb: 2 }}>
        {t(
          "Assign coaches to mentees or end a coaching assignment. Changes take effect the next time the coach signs in.",
        )}
      </Typography>

      {programBlockedByEnv ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t("Loading your programs…")}
          </Typography>
        </Box>
      ) : programsFailed ? (
        <Alert severity="error" sx={{ maxWidth: 640 }}>
          <AlertTitle>{t("Failed to load your programs")}</AlertTitle>
          {programsError ? (
            <Typography
              variant="caption"
              component="pre"
              sx={{
                mt: 1,
                mb: 1,
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
                fontSize: "0.75rem",
                color: "text.secondary",
              }}
            >
              {programsError}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button variant="contained" size="small" onClick={retryLoadPrograms}>
              {t("Retry")}
            </Button>
          </Stack>
        </Alert>
      ) : !hasPrograms ? (
        <Alert severity="info">
          {t(
            "You don't administer any programs, so this page has nothing to configure. A SystemAdmin who isn't a ProgramAdmin on any program will see this — that's expected: this surface is per-program.",
          )}
        </Alert>
      ) : (
        // Same layout language as Users: filters and the assign row
        // sit on the page, the grants table is the outlined surface.
        <Stack spacing={2}>
          {/* Program picker — shrink + notched, same as Users. */}
          <FormControl size="small" sx={{ width: 300 }}>
            <InputLabel shrink id="coaching-program-label">
              {t("Program")}
            </InputLabel>
            <Select
              labelId="coaching-program-label"
              label={t("Program")}
              displayEmpty
              value={selectedProgramId}
              input={<OutlinedInput notched label={t("Program")} />}
              onChange={(e) => setSelectedProgramId(String(e.target.value))}
              data-testid="coaching-program-picker"
            >
              {programs.map((program) => (
                <MenuItem key={program.id} value={program.id}>
                  {program.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {saveError && (
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}
          {saveSuccess && (
            <Alert severity="success" onClose={() => setSaveSuccess(null)}>
              {saveSuccess}
            </Alert>
          )}

          {/* Assign form comes first: it's the ProgramAdmin's primary
              action on this page. The current-assignments table is a
              read-mostly reference below. */}
          <AssignCoachCard
            role={assignRole}
            managedRoles={managedRoles}
            onRoleChange={setAssignRole}
            attendings={attendings}
            attendingsStatus={attendingsStatus}
            attendingsError={attendingsError}
            assignCoach={assignCoach}
            onAssignCoachChange={setAssignCoach}
            disabledCoachEmails={disabledCoachEmails}
            mentees={traineeMentees}
            menteesStatus={menteesStatus}
            menteesError={mentees.error ?? attendingsError}
            assignMenteeId={assignMenteeId}
            onAssignMenteeChange={setAssignMenteeId}
            disabledMenteeIds={disabledMenteeIds}
            saving={savingRow?.kind === "assign"}
            canAssign={canAssign}
            onAssign={onAssign}
          />

          <ExistingGrantsCard
            status={grants.status}
            error={grants.error}
            coaches={coaches}
            attendingNameByEmail={attendingNameByEmail}
            role={managedRoles[0] ?? ""}
            savingRow={savingRow}
            onRevoke={onRevoke}
          />
        </Stack>
      )}
    </PageWrapper>
  );
};

// -----------------------------------------------------------------------------
// Current coaching assignments — one row per coach × mentee, with the
// coach cell rowSpanned across all their mentees.
// -----------------------------------------------------------------------------

const ExistingGrantsCard = ({
  status,
  error,
  coaches,
  attendingNameByEmail,
  role,
  savingRow,
  onRevoke,
}: {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  coaches: Coach[];
  // Shared email → display name map. Built in `CoachingPage` from the
  // attendings list so save messages and this table can't drift on
  // "what to call this coach". A coach missing from the map (unusual —
  // coaching in a program they aren't Attending in) falls back to
  // email.
  attendingNameByEmail: Map<string, string>;
  role: string;
  savingRow: { kind: SaveKind; email: string; scopeId?: string } | null;
  onRevoke: (coach: Coach, row: RoleScope) => Promise<void>;
}) => {
  const { t } = useTranslation();

  return (
    // Heading sits on the page; the table is the Users-style outlined
    // surface (same chrome as DataGrid on /users).
    <Stack spacing={1}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t("Current coaching assignments")}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        {t(
          "Some assignments are set here; others are synced from your training program's system. You can only remove the ones set here.",
        )}
      </Typography>

      {status === "idle" && (
        <Typography variant="body2" color="text.secondary">
          {t("Choose a program to see its coaching assignments.")}
        </Typography>
      )}
      {status === "loading" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t("Loading coaching assignments…")}
          </Typography>
        </Box>
      )}
      {status === "failed" && (
        <Alert severity="error">
          <AlertTitle>
            {t("Couldn't load the coaching assignments")}
          </AlertTitle>
          {t("Something went wrong — please try again.")}
          {error ? (
            <>
              {" "}
              <code>{error}</code>
            </>
          ) : null}
        </Alert>
      )}
      {status === "succeeded" && coaches.length === 0 && (
        <Alert severity="info">
          {t("No coaching assignments yet. Use the form above to add one.")}
        </Alert>
      )}
      {status === "succeeded" && coaches.length > 0 && (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            boxShadow: "none",
            "& .MuiTableCell-root": { py: 1 },
            "& .MuiTableCell-head": { fontWeight: 500 },
          }}
        >
          <Table data-testid="coaching-grants-table">
            <TableHead>
              <TableRow>
                <TableCell>{t("Coach")}</TableCell>
                <TableCell>{t("Mentee")}</TableCell>
                <TableCell width={64} align="right">
                  {" "}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coaches.map((coach) => {
                const coachDisplay =
                  attendingNameByEmail.get(coach.email.toLowerCase()) ??
                  coach.email;
                return coach.mentees.map((row, idx) => {
                  const menteeLabel = row.scope_name ?? row.scope_id;
                  const admin = isAdminOwned(row.data_source_name);
                  const isSaving =
                    savingRow?.kind === "revoke" &&
                    savingRow.email === coach.email &&
                    savingRow.scopeId === row.scope_id;
                  return (
                    <TableRow
                      key={`${coach.email}::${row.role}::${row.scope_id}`}
                      data-coach-email={coach.email}
                      data-mentee-scope-id={row.scope_id}
                      hover
                    >
                      {idx === 0 ? (
                        <TableCell
                          rowSpan={coach.mentees.length}
                          sx={{ verticalAlign: "top" }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {coachDisplay}
                          </Typography>
                          {coachDisplay !== coach.email && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {coach.email}
                            </Typography>
                          )}
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            // `space-between` pins the "Synced" chip
                            // to the right edge of every Mentee cell,
                            // so the chips form a clean vertical stripe
                            // instead of drifting with the name width.
                            // ADMIN rows have only the name, which stays
                            // left-aligned.
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <span>{menteeLabel}</span>
                          {!admin && (
                            // Non-ADMIN rows come from an upstream sync
                            // (medqip / SSO / etc.). We surface that as
                            // a subtle "Synced" pill so the disabled
                            // trash icon isn't unexplained, without
                            // dragging the provenance vocabulary
                            // (SIMULATED_SSO / MEDQIP / ADMIN) into the
                            // end-user UI.
                            <Chip
                              size="small"
                              label={t("Synced")}
                              variant="outlined"
                              sx={{
                                height: 20,
                                "& .MuiChip-label": {
                                  px: 0.75,
                                  fontSize: "0.7rem",
                                },
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {admin && row.role === role ? (
                          <Tooltip
                            title={t("End this coaching assignment")}
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => onRevoke(coach, row)}
                                disabled={savingRow !== null}
                                data-testid="coaching-revoke-button"
                                aria-label={t(
                                  "End coaching assignment for {{name}}",
                                  { name: menteeLabel },
                                )}
                              >
                                {isSaving ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteOutlineIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            title={t(
                              "This assignment was synced from your training program's system. Update it there.",
                            )}
                          >
                            <span>
                              <IconButton size="small" disabled>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

// -----------------------------------------------------------------------------
// Assign form — pick a coach and one mentee
// -----------------------------------------------------------------------------

const AssignCoachCard = ({
  role,
  managedRoles,
  onRoleChange,
  attendings,
  attendingsStatus,
  attendingsError,
  assignCoach,
  onAssignCoachChange,
  disabledCoachEmails,
  mentees,
  menteesStatus,
  menteesError,
  assignMenteeId,
  onAssignMenteeChange,
  disabledMenteeIds,
  saving,
  canAssign,
  onAssign,
}: {
  role: string;
  managedRoles: string[];
  onRoleChange: (role: string) => void;
  attendings: Grantee[];
  attendingsStatus: "idle" | "loading" | "succeeded" | "failed";
  attendingsError: string | null;
  assignCoach: Grantee | null;
  onAssignCoachChange: (g: Grantee | null) => void;
  // Coach emails to grey out — computed by the parent from the
  // currently-selected mentee's existing coaches (all provenance). Empty
  // when no mentee is selected. See `existingPairs` in `CoachingPage`
  // for the derivation, including why provenance is deliberately
  // ignored here.
  disabledCoachEmails: Set<string>;
  mentees: { id: string; label: string }[];
  menteesStatus: "idle" | "loading" | "succeeded" | "failed";
  menteesError: string | null;
  assignMenteeId: string;
  onAssignMenteeChange: (id: string) => void;
  // Mentee scope_ids to grey out — the coach-side twin of the above.
  disabledMenteeIds: Set<string>;
  saving: boolean;
  canAssign: boolean;
  onAssign: () => void;
}) => {
  const { t } = useTranslation();

  // The disabled-suffix label. Rendered next to the option in a muted
  // MUI Typography secondary so a keyboard-only user still sees WHY the
  // click is refused, not just that it is. Uses the parent's role name
  // implicitly — "already coaches this mentee" is Coaching-specific
  // wording (Rotations on SE-1883 will phrase it differently).
  const alreadyPaired = t("(already paired)");

  return (
    // Users-style toolbar: heading + filter row, no wrapping card.
    <Stack spacing={1}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t("Assign a coach to a mentee")}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        {t(
          "Pick a coach (an Attending or existing Coach in this program) and a mentee (a Trainee), then click Assign. Assigning also gives the coach the Coach role in this program. A coach can have multiple mentees — adding a new one won't remove the others.",
        )}
      </Typography>

      <Stack spacing={2}>
        {managedRoles.length > 1 && (
          <FormControl size="small" sx={{ width: 300 }}>
            <InputLabel shrink id="coaching-role-label">
              {t("Role")}
            </InputLabel>
            <Select
              labelId="coaching-role-label"
              label={t("Role")}
              displayEmpty
              value={role}
              input={<OutlinedInput notched label={t("Role")} />}
              onChange={(e) => onRoleChange(String(e.target.value))}
              data-testid="coaching-role-picker"
            >
              {managedRoles.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "flex-end" }}
        >
          <Autocomplete
            size="small"
            options={attendings}
            getOptionLabel={(o) => `${o.displayName} <${o.email}>`}
            isOptionEqualToValue={(a, b) => a.email === b.email}
            value={assignCoach}
            onChange={(_e, v) => onAssignCoachChange(v)}
            disabled={
              attendingsStatus !== "succeeded" || attendings.length === 0
            }
            loading={attendingsStatus === "loading"}
            getOptionDisabled={(o) => disabledCoachEmails.has(o.email)}
            sx={{ flex: 1, minWidth: 0 }}
            renderOption={(props, option) => {
              const paired = disabledCoachEmails.has(option.email);
              return (
                <li {...props} key={option.email}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <span>{`${option.displayName} <${option.email}>`}</span>
                    {paired && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: "auto" }}
                      >
                        {alreadyPaired}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Coach")}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  shrink: true,
                }}
                inputProps={{
                  ...params.inputProps,
                  "data-testid": "coaching-coach-picker",
                }}
              />
            )}
          />

          <FormControl
            size="small"
            sx={{ flex: 1, minWidth: 0 }}
            disabled={
              menteesStatus !== "succeeded" || mentees.length === 0 || !role
            }
          >
            <InputLabel shrink id="coaching-mentee-label">
              {t("Mentee")}
            </InputLabel>
            <Select
              labelId="coaching-mentee-label"
              label={t("Mentee")}
              displayEmpty
              value={assignMenteeId}
              input={<OutlinedInput notched label={t("Mentee")} />}
              onChange={(e) => onAssignMenteeChange(String(e.target.value))}
              data-testid="coaching-mentee-picker"
            >
              {mentees.map((m) => {
                const paired = disabledMenteeIds.has(m.id);
                return (
                  <MenuItem
                    key={m.id}
                    value={m.id}
                    disabled={paired}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <span>{m.label}</span>
                    {paired && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: "auto" }}
                      >
                        {alreadyPaired}
                      </Typography>
                    )}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            size="small"
            onClick={onAssign}
            disabled={!canAssign}
            data-testid="coaching-assign-button"
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
            sx={{ flexShrink: 0, height: 40 }}
          >
            {saving ? t("ASSIGNING…") : t("ASSIGN")}
          </Button>
        </Stack>
        {attendingsStatus === "failed" && (
          <Alert severity="error">
            {t("Couldn't load the list of possible coaches")}
            {attendingsError ? `: ${attendingsError}` : ""}
          </Alert>
        )}
        {attendingsStatus === "succeeded" && attendings.length === 0 && (
          <Alert severity="info">
            {t(
              "This program has no Attendings yet, so there's nobody to make a coach. Invite an Attending under Users → New User, then come back.",
            )}
          </Alert>
        )}
        {menteesStatus === "succeeded" && mentees.length === 0 && (
          <Alert severity="info" data-testid="coaching-empty-mentees">
            {t(
              "This program has no Trainees on its roster yet, so there's nobody to coach. Mentees are the program's Trainees rather than being created here.",
            )}
          </Alert>
        )}
        {menteesStatus === "loading" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              {t("Loading possible mentees…")}
            </Typography>
          </Box>
        )}
        {menteesStatus === "failed" && (
          <Alert severity="error">
            {t("Couldn't load the list of possible mentees")}
            {menteesError ? `: ${menteesError}` : ""}
          </Alert>
        )}
      </Stack>
    </Stack>
  );
};
